import * as React from "react";
import { useDialog } from "@shared-ui/dialog-context";
import { LinksDialogContext } from "components/flexComponents/Links/GridView/LinkDialogContext";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import Links from "components/flexComponents/Links/Links";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkIcon } from "uitk-react-icons";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkButton } from "uitk-react-button";
import { UitkLink, UitkLinkAlign } from "uitk-react-link";
import { ExtendedFTNode } from "typings/flexFramework/FlexComponent";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { DialogProps, LinksFTNode, LinksGridItemProps } from "../typings";

export const LinksGridItem = withStores("flexModuleModelStore")(
  observer((props: LinksGridItemProps) => {
    const { view, group, id, flexModuleModelStore, linkGridRef } = props;
    const [, dialogActions] = useDialog(`linksDialog-${id}`);
    const flexTemplateNode = group.flexTemplateNode;
    const { childNodes } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(flexTemplateNode.id);
    const linksDialogContext = LinksDialogContext;

    const { title, subheading, dialogHeading, subHeadingAlignment } = model;
    const linkModules: LinksFTNode[] = childNodes.filter(
      (templateNode: ExtendedFTNode) => templateNode.options.flexComponentName === "links"
    );
    const openDialog = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
      linksDialogContext.dialogHeading = dialogHeading;
      linksDialogContext.modules = linkModules;
      dialogActions.openDialog();
    };

    const keyHelper = new ItemKeyHelper(`LinksGridItem-${id}`);

    const links = linkModules?.map((module) => (
      <div aria-hidden="true" hidden key={keyHelper.next()}>
        <Links flexTemplateNode={module} key={keyHelper.next()} />
      </div>
    ));

    const dialogProps: DialogProps = { title, subheading, subHeadingAlignment, openDialog, linkGridRef };

    let dialogView;
    if (view === "row-trigger") {
      dialogView = <DialogViewWithIcon {...dialogProps} />;
    } else if (view === "standard-link") {
      dialogView = <DialogViewLinkableText {...dialogProps} />;
    }

    return (
      <div className="LinksGridItem" key={keyHelper.next()}>
        <UitkCard className={view}>{dialogView}</UitkCard>
        <div className="is-visually-hidden">{links}</div>
      </div>
    );
  })
);

const setReferenceOpener = (linkGridRef: React.MutableRefObject<any>, elementRef: React.RefObject<HTMLElement>) => {
  React.useEffect(() => {
    linkGridRef.current = elementRef.current;
  }, []);
};

const DialogViewWithIcon: React.FC<DialogProps> = observer(({ title, subheading, openDialog, linkGridRef }) => {
  const buttonRef = React.createRef<HTMLButtonElement>();

  setReferenceOpener(linkGridRef, buttonRef);

  return (
    <UitkCardContentSection>
      <UitkButton onClick={openDialog} isFullWidth aria-label={title} domRef={buttonRef}>
        <UitkLayoutFlex justifyContent="space-between" alignItems="center" className="LinksGridItem">
          <UitkLayoutFlexItem>
            <div className="uitk-type-left">
              <UitkHeading tag="h3" size={7}>
                {title}
              </UitkHeading>
              {subheading && <UitkSubheading>{subheading}</UitkSubheading>}
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkIcon name="chevron_right" />
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkButton>
    </UitkCardContentSection>
  );
});

const DialogViewLinkableText: React.FC<DialogProps> = observer(
  ({ title, subheading, subHeadingAlignment, openDialog, linkGridRef }) => {
    const linkRef = React.createRef<HTMLAnchorElement>();

    setReferenceOpener(linkGridRef, linkRef);

    return (
      <UitkCardContentSection>
        <UitkLayoutFlexItem>
          <UitkLayoutFlex justifyContent={subHeadingAlignment === "center" ? "center" : "start"}>
            <UitkLayoutFlexItem className={subHeadingAlignment === "center" ? "links-item-center" : ""}>
              <div>
                <UitkHeading tag="h3" size={7}>
                  {title}
                </UitkHeading>
                <UitkLink
                  onClick={openDialog}
                  align={subHeadingAlignment === "center" ? UitkLinkAlign.CENTER : UitkLinkAlign.LEFT}
                >
                  <a href="" role="button" ref={linkRef}>
                    {subheading}
                  </a>
                </UitkLink>
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
      </UitkCardContentSection>
    );
  }
);

export default LinksGridItem;
